'use client'

import { motion } from 'framer-motion'
import { Award, Target, Heart, Users, CheckCircle2, Mail, Clock, TrendingUp, Shield, Globe, ExternalLink } from 'lucide-react'
import Link from 'next/link'
import company from '@/data/company.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { useCounter } from '@/hooks/useCounter'

const values = [
  {
    icon: Shield,
    title: 'Integrity & Compliance',
    description: 'We operate with full transparency and strict adherence to all UAE laws and regulations.',
    color: '#1D4ED8',
    bg: '#DBEAFE',
  },
  {
    icon: TrendingUp,
    title: 'Speed & Efficiency',
    description: 'We streamline every process to deliver results faster without compromising accuracy.',
    color: '#D97706',
    bg: '#FEF3C7',
  },
  {
    icon: Users,
    title: 'Client-First Approach',
    description: 'Every decision is made with our clients\' best interests at heart.',
    color: '#059669',
    bg: '#D1FAE5',
  },
  {
    icon: Globe,
    title: 'Multi-Emirates Coverage',
    description: 'Direct relationships with government departments across all 7 UAE Emirates.',
    color: '#7C3AED',
    bg: '#EDE9FE',
  },
]

const timeline = [
  { year: '2014', event: 'Founded in Dubai with a mission to simplify government documentation' },
  { year: '2016', event: 'Expanded services to cover all 7 UAE Emirates' },
  { year: '2018', event: 'Reached 2,500+ satisfied clients milestone' },
  { year: '2020', event: 'Launched Golden Visa assistance service amidst UAE reforms' },
  { year: '2022', event: 'Processed over 20,000 documents — fastest turnaround on record' },
  { year: '2024', event: '10,000+ clients and 50,000+ documents processed with 98% success rate' },
]

export function AboutSection() {
  const { ref, isVisible } = useScrollAnimation()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  }

  return (
    <section id="about" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="container-narrow">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-badge">
            <Award className="w-4 h-4" />
            About Us
          </span>
          <h2 className="section-title">A Decade of Trusted Service in UAE</h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            Since 2014, Al Ishrat has been the go-to partner for document clearing and government services,
            serving thousands of individuals and businesses across the Emirates.
          </p>
        </motion.div>

        {/* Story + Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B] mb-5">
              Your Reliable Government Relations Partner
            </h3>
            <p className="text-[#475569] text-base leading-relaxed mb-6">
              {company.company.longDescription}
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'Experienced Government Relations Team',
                'Direct Access to All UAE Ministries',
                'Multi-Emirates Service Coverage',
                'Transparent & Fully Compliant Processes',
                '24/7 Client Support Available',
                'Same-Day Processing for Urgent Cases',
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1D4ED8] flex-shrink-0" />
                  <span className="text-[#1E293B] font-medium text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-4 flex-wrap">
              <Link href="/contact" className="btn-primary gap-2">
                Get Started Today
              </Link>
              <Link href="/process" className="btn-secondary gap-2">
                Our Process
              </Link>
            </div>
          </motion.div>

          {/* Right - Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 gap-5"
          >
            {company.company.stats.map((stat, idx) => {
              const colors = [
                { bg: '#EFF6FF', accent: '#1D4ED8', border: '#DBEAFE' },
                { bg: '#FFFBEB', accent: '#D97706', border: '#FDE68A' },
                { bg: '#F0FDF4', accent: '#059669', border: '#BBF7D0' },
                { bg: '#FAF5FF', accent: '#7C3AED', border: '#DDD6FE' },
              ]
              const color = colors[idx % colors.length]
              return (
                <motion.div
                  key={stat.id}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl p-6 text-center border transition-all duration-300"
                  style={{ background: color.bg, borderColor: color.border }}
                >
                  <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: color.accent }}>
                    <StatCounter target={parseInt(stat.value)} isVisible={isVisible} suffix={stat.value.replace(/\d/g, '')} />
                  </div>
                  <p className="text-[#1E293B] font-bold text-sm mb-1">{stat.label}</p>
                  <p className="text-[#64748B] text-xs">{stat.description}</p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>

        {/* Company Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20"
        >
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B]">Our Journey</h3>
            <p className="text-[#64748B] mt-2">A decade of milestones and growth</p>
          </div>
          <div className="relative">
            {/* Connecting line - desktop */}
            <div className="hidden md:block absolute top-6 left-0 right-0 h-0.5 bg-gradient-to-r from-[#DBEAFE] via-[#1D4ED8] to-[#D97706]" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {timeline.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="relative flex flex-col items-center text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center font-bold text-xs z-10 mb-3 shadow-soft">
                    {item.year}
                  </div>
                  <p className="text-[#475569] text-xs leading-relaxed">{item.event}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="rounded-2xl p-8"
            style={{ background: '#EFF6FF', borderLeft: '4px solid #1D4ED8' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="icon-box-primary">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-[#1E293B]">Our Mission</h3>
            </div>
            <p className="text-[#475569] leading-relaxed">{company.company.mission}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl p-8"
            style={{ background: '#FFFBEB', borderLeft: '4px solid #D97706' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="icon-box-accent">
                <Heart className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-[#1E293B]">Our Vision</h3>
            </div>
            <p className="text-[#475569] leading-relaxed">{company.company.vision}</p>
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B] mb-2">Our Core Values</h3>
          <p className="text-[#64748B]">The principles that guide everything we do</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-20"
        >
          {values.map((val, idx) => {
            const Icon = val.icon
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="bg-white rounded-2xl p-6 border border-[#DBEAFE] text-center hover:-translate-y-1 transition-all duration-300 group"
                style={{ boxShadow: '0 4px 6px rgba(29,78,216,0.07)' }}
              >
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: val.bg, color: val.color }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <h4 className="font-bold text-[#1E293B] mb-2">{val.title}</h4>
                <p className="text-[#64748B] text-sm leading-relaxed">{val.description}</p>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12 text-center"
        >
          <span className="section-badge">
            <Users className="w-4 h-4" />
            Our Team
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B]">Meet Our Expert Team</h3>
          <p className="text-[#64748B] mt-2">Specialists with decades of combined UAE government experience</p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {company.company.team.map((member) => (
            <motion.div
              key={member.id}
              variants={itemVariants}
              className="bg-white rounded-2xl border border-[#DBEAFE] p-6 text-center hover:-translate-y-1 transition-all duration-300 group"
              style={{ boxShadow: '0 4px 6px rgba(29,78,216,0.07)' }}
            >
              {/* Avatar Placeholder */}
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#3B82F6] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform duration-300">
                {member.name.charAt(0)}
              </div>
              <h4 className="font-bold text-[#1E293B] text-base mb-1">{member.name}</h4>
              <p className="text-[#1D4ED8] text-xs font-semibold mb-2">{member.position}</p>
              <p className="text-[#64748B] text-xs mb-3 leading-relaxed">{member.expertise}</p>
              <div className="flex items-center justify-center gap-1 mb-4">
                <Clock className="w-3 h-3 text-[#D97706]" />
                <span className="text-[#D97706] text-xs font-semibold">{member.experience}</span>
              </div>
              <div className="flex items-center justify-center gap-3 pt-3 border-t border-[#DBEAFE]">
                <a
                  href={member.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center hover:bg-[#1D4ED8] hover:text-white transition-all duration-200"
                  aria-label={`${member.name} LinkedIn`}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={`mailto:${member.social.email}`}
                  className="w-8 h-8 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center hover:bg-[#1D4ED8] hover:text-white transition-all duration-200"
                  aria-label={`Email ${member.name}`}
                >
                  <Mail className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  )
}

function StatCounter({ target, isVisible, suffix = '' }: { target: number; isVisible: boolean; suffix?: string }) {
  const count = useCounter(target, 2000, isVisible)
  return <>{isNaN(count) ? target : count}{suffix}</>
}
